import React, { useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import arePropsEqual from 'react-fast-compare';
import { useSelector } from 'react-redux';
import {
  pushToDataLayer,
  logNativeEvent,
  dataLayerEventLogger as logger,
} from '../utils/ga';
import { isNativeSelector } from '../redux/reducers/app/params';
import { DATA_LAYER_EVENT, DATA_LAYER_EVENT_CATEGORY } from '../constants/ga';
import { sendAnalyticsData } from '../services/analytics';

const withAnalytics = (WrappedComponent) => {
  const AnalyticsWrapper = ({
    forwardedRef,
    dataLayerEventData,
    onClick,
    analyticsData,
    ...rest
  }) => {
    const isNative = useSelector(isNativeSelector);

    const handleClick = useCallback(
      (event, ...args) => {
        const eventToPublish = {
          event: DATA_LAYER_EVENT,
          eventCategory: DATA_LAYER_EVENT_CATEGORY,
          ...dataLayerEventData,
        };

        if (analyticsData) {
          sendAnalyticsData(analyticsData);
        }

        try {
          if (isNative) {
            logger.log('native event:\n\n', eventToPublish);
            logNativeEvent({
              name: dataLayerEventData.eventAction,
              params: eventToPublish,
            });
          } else {
            logger.log('web event:\n\n', eventToPublish);
            pushToDataLayer(eventToPublish);
          }
        } catch (err) {
          console.error('Error during pushToDataLayer. Details: ', err);
        }

        if (typeof onClick === 'function') {
          onClick(event, ...args);
        }
      },
      [isNative, onClick, dataLayerEventData, analyticsData]
    );

    return (
      <WrappedComponent ref={forwardedRef} onClick={handleClick} {...rest} />
    );
  };

  return memo(
    React.forwardRef((props, ref) => {
      return <AnalyticsWrapper {...props} forwardedRef={ref} />;
    }),
    arePropsEqual
  );
};

withAnalytics.propTypes = {
  dataLayerEventData: PropTypes.shape({
    eventAction: PropTypes.string.isRequired,
    provider_id: PropTypes.string,
    category_id: PropTypes.string,
    message: PropTypes.string,
    bonus_id: PropTypes.string,
    game_id: PropTypes.string,
    wagering: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    position_index: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    sectionIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  analyticsData: PropTypes.shape({
    eventAction: PropTypes.string.isRequired,
    provider_id: PropTypes.string,
    category_id: PropTypes.string,
    message: PropTypes.string,
    bonus_id: PropTypes.string,
    game_id: PropTypes.string,
    wagering_id: PropTypes.string,
    position_index: PropTypes.number,
    sectionIndex: PropTypes.number,
  }),
  onClick: PropTypes.func,
};
withAnalytics.defaultProps = {
  dataLayerEventData: {},
};

export default withAnalytics;
